﻿import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
export * from "@microsoft/applicationinsights-web";
import history from "../history";
// We only import the type definitions from the dev dependency here, excluding the actual module from the production bundle.
import type { default as OpenReplayAPI, Options as OpenReplayOptions } from "@openreplay/tracker";

export type UserContext = {
    emailAddress: string;
};

let appInsights: ApplicationInsights;
let openReplay: OpenReplayAPI;
let userContext: UserContext;

export function initAppInsights(appInsightsInstrumentationKey: string, tenantName: string) {
    const reactPlugin = new ReactPlugin();
    appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: appInsightsInstrumentationKey,
            extensions: [reactPlugin],
            endpointUrl: "/proxy/logging", // Reverse-proxy to App Insights API endpoint to circumvent ad-blockers...
            extensionConfig: {
                [reactPlugin.identifier]: { history: history }
            }
        }
    });
    appInsights.loadAppInsights();

    // Add the tenant name as a property to each metric...
    appInsights.addTelemetryInitializer((telemetryItem) => {
        telemetryItem.data["tenantName"] = tenantName;
        telemetryItem.tags["ai.cloud.role"] = "React SPA";
        telemetryItem.tags["ai.application.ver"] = window.__APP_VERSION__;
    });

    if (userContext) {
        appInsights.setAuthenticatedUserContext(userContext.emailAddress);
    }
};

const DEFAULT_OPEN_REPLAY_OPTIONS = {
    obscureInputDates: false,
    obscureInputEmails: false,
    obscureInputNumbers: false,
    obscureTextEmails: false,
    obscureTextNumbers: false,
    defaultInputMode: 0,
} as OpenReplayOptions;

export async function initOpenReplay(tenantName: string, projectKey: string, ingestPoint?: string | undefined, extraOptionsJson?: string | undefined) {
    // The OpenReplay tracker is quite a heavy dependency and it's not used by many tenants,
    // so we lazy-load the module from a CDN at runtime, as required...
    const { default: OpenReplayAPI } = await import("https://cdn.jsdelivr.net/npm/@openreplay/tracker@18/+esm" as const);
    openReplay = new OpenReplayAPI(
        ingestPoint ?
            { projectKey, ingestPoint, ...DEFAULT_OPEN_REPLAY_OPTIONS, ...JSON.parse(extraOptionsJson || "{}") } :
            { projectKey, ...DEFAULT_OPEN_REPLAY_OPTIONS, ...JSON.parse(extraOptionsJson || "{}") }
    );
    openReplay.start();
    openReplay.setMetadata("tenantName", tenantName);

    if (userContext) {
        openReplay.setUserID(userContext.emailAddress);
    }
};

export function setAuthenticatedUserContext(emailAddress: string) {
    userContext = { emailAddress };

    if (appInsights) {
        appInsights.setAuthenticatedUserContext(emailAddress);
    }

    if (openReplay) {
        openReplay.setUserID(emailAddress);
    }
};

export {
    appInsights as logger,
    openReplay as openReplayTracker,
};